import { useEffect, useState } from "react";
import { CloudOff, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { listPending, removePending, countPending } from "@/lib/offlineQueue";
import { logger } from "@/lib/logger";

async function drainOnce() {
  const pending = await listPending();
  if (pending.length === 0) return { sent: 0, failed: 0 };
  let sent = 0;
  let failed = 0;
  for (const item of pending) {
    try {
      const blob = new Blob([item.photo_buffer], { type: item.photo_type });
      const fd = new FormData();
      fd.append("employee_name", item.employee_name);
      fd.append("employee_id", item.employee_id);
      fd.append("reason", item.reason);
      fd.append("photo", new File([blob], item.photo_name, { type: item.photo_type }));
      await api.post("/submissions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await removePending(item.id);
      sent += 1;
    } catch (err) {
      logger.warn("[offline-queue] drain item failed", err);
      failed += 1;
      // Stop after the first failure to avoid hammering the server while still offline.
      break;
    }
  }
  return { sent, failed };
}

export default function OfflineQueueIndicator() {
  const [count, setCount] = useState(0);
  const [draining, setDraining] = useState(false);

  const refresh = async () => {
    try {
      setCount(await countPending());
    } catch (err) {
      logger.warn("[offline-queue] count failed", err);
    }
  };

  useEffect(() => {
    refresh();
    const drain = async () => {
      if (!navigator.onLine) return;
      setDraining(true);
      const { sent } = await drainOnce();
      setDraining(false);
      await refresh();
      if (sent > 0) {
        toast.success(
          sent === 1
            ? "Your queued story was uploaded."
            : `${sent} queued stories were uploaded.`
        );
      }
    };
    window.addEventListener("online", drain);
    window.addEventListener("ws-queue-changed", refresh);
    drain(); // First-load attempt
    return () => {
      window.removeEventListener("online", drain);
      window.removeEventListener("ws-queue-changed", refresh);
    };
  }, []);

  if (count === 0 && !draining) return null;

  return (
    <div
      data-testid="offline-queue-indicator"
      className="fixed top-20 right-4 z-50 bg-[#09090B] text-white border-2 border-[#FFCC00] ws-hard-shadow-sm px-4 py-2 flex items-center gap-2"
    >
      {draining ? (
        <Loader2 strokeWidth={2.5} className="w-4 h-4 animate-spin text-[#FFCC00]" />
      ) : (
        <CloudOff strokeWidth={2.5} className="w-4 h-4 text-[#FFCC00]" />
      )}
      <span className="ws-label text-[#FFCC00]">
        {draining ? "Uploading…" : `${count} queued`}
      </span>
      {!draining && count > 0 && <CheckCircle2 strokeWidth={2.5} className="w-3.5 h-3.5 text-white opacity-60" />}
    </div>
  );
}
